// src/components/LowRound/LowRound.js
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
} from "@mui/material";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase-config";
import { useAuth } from "../../context/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import WeekGroup from "./WeekGroup";
import RoundDetail from "./RoundDetail";
import {
  getCompetitionWindowStartForDate,
  getCompetitionDeadlineForWindowStart,
} from "../../utils/competitionWindow";

// Helper: Convert a Firestore timestamp or ISO string to a Date.
const toDate = (value) =>
  !value
    ? null
    : typeof value === "object" && value.seconds
    ? new Date(value.seconds * 1000)
    : new Date(value);

const LowRoundContent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [groupedRounds, setGroupedRounds] = useState({});
  // userNames mapping: userId -> { fullName, bagTag, photoURL }
  const [userNames, setUserNames] = useState({});
  // selectedData holds: { round, weekDate, placementIndex, playerName, playerBagTag, playerPhotoURL }
  const [selectedData, setSelectedData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch rounds from all users.
        const usersSnap = await getDocs(collection(db, "users"));
        const allRounds = [];
        await Promise.all(
          usersSnap.docs.map(async (userDoc) => {
            try {
              const scorecardsSnap = await getDocs(
                collection(db, "users", userDoc.id, "scorecards")
              );
              scorecardsSnap.forEach((docSnap) => {
                const data = docSnap.data();
                allRounds.push({ id: docSnap.id, ...data, userId: userDoc.id });
              });
            } catch (err) {
              // Ignore errors for individual users.
            }
          })
        );
        // Sort rounds descending by roundDate.
        allRounds.sort((a, b) => toDate(b.roundDate) - toDate(a.roundDate));

        // Group rounds by competition window (only isCompetition rounds).
        const groups = {};
        allRounds
          .filter((round) => round.isCompetition)
          .forEach((round) => {
            const date = toDate(round.roundDate);
            if (!date) return;
            const compStart = getCompetitionWindowStartForDate(date);
            const compDeadline = getCompetitionDeadlineForWindowStart(compStart);
            if (date >= compStart && date <= compDeadline) {
              const weekKey = compStart.getTime();
              groups[weekKey] = groups[weekKey] || [];
              groups[weekKey].push(round);
            }
          });
        // Always include the current competition window.
        const currentWindowStart = getCompetitionWindowStartForDate(new Date());
        const currentKey = currentWindowStart.getTime();
        if (!groups[currentKey]) groups[currentKey] = [];
        setGroupedRounds(groups);

        // Fetch user names, bag tags, and photo URLs.
        const userIdSet = new Set(allRounds.map((r) => r.userId));
        const namesMap = {};
        await Promise.all(
          [...userIdSet].map(async (userId) => {
            try {
              const userDocSnap = await getDoc(doc(db, "users", userId));
              if (userDocSnap.exists()) {
                const data = userDocSnap.data();
                const fullName =
                  `${data.firstName || ""} ${data.lastName || ""}`.trim() ||
                  "Unknown Player";
                const bagTag = data.bagTag || "N/A";
                const photoURL = data.photoURL || "";
                namesMap[userId] = { fullName, bagTag, photoURL };
              } else {
                namesMap[userId] = { fullName: "Unknown Player", bagTag: "N/A", photoURL: "" };
              }
            } catch (e) {
              namesMap[userId] = { fullName: "Unknown Player", bagTag: "N/A", photoURL: "" };
            }
          })
        );
        setUserNames(namesMap);
      } catch (e) {
        setError("Error fetching rounds.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Sorted week keys.
  const sortedWeekKeys = Object.keys(groupedRounds)
    .map(Number)
    .sort((a, b) => b - a);

  // When a round is selected, build the selectedData using user info.
  const handleSelectRound = (round, weekDate, placementIndex) => {
    const info =
      userNames[round.userId] || { fullName: "Unknown Player", bagTag: "N/A", photoURL: "" };
    setSelectedData({
      round,
      weekDate,
      placementIndex,
      playerName: info.fullName,
      playerBagTag: info.bagTag,
      playerPhotoURL: info.photoURL,
    });
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Sunday's Low Round
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Weekly regional and team competition play.
      </Typography>
      {loading ? (
        <Grid container justifyContent="center">
          <CircularProgress />
        </Grid>
      ) : error ? (
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      ) : sortedWeekKeys.length === 0 ? (
        <Typography variant="body1">No rounds found.</Typography>
      ) : (
        sortedWeekKeys.map((weekKey) => (
          <WeekGroup
            key={weekKey}
            weekKey={weekKey}
            roundsForWeek={groupedRounds[weekKey]}
            userNames={userNames}
            onSelectRound={(round, weekDate, placementIndex) =>
              handleSelectRound(round, weekDate, placementIndex)
            }
            navigateToScorecard={() => navigate("/scorecard")}
          />
        ))
      )}
      {selectedData && (
        <Dialog
          open={Boolean(selectedData)}
          onClose={() => setSelectedData(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogContent dividers>
            <RoundDetail
              round={selectedData.round}
              competitionWeek={selectedData.weekDate}
              placementIndex={selectedData.placementIndex}
              playerName={selectedData.playerName}
              playerBagTag={selectedData.playerBagTag}
              playerPhotoURL={selectedData.playerPhotoURL}
              isCompetition={selectedData.round.isCompetition}
              showProfileInfo={true} // FULL PROFILE info header
              onClose={() => setSelectedData(null)}
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              sx={{ mt: 3 }}
              onClick={() => setSelectedData(null)}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

const LowRound = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <LowRoundContent />;
};

export default LowRound;